import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "staysure-component-library";

import RichText from "@/components/atoms/rich-text/rich-text";
import {
  CONSENT_MODAL,
  CONSENT_NO_MODAL,
  CONSENT_YES_MODAL,
  CONSENT_YES_MODAL_WITH_ZERO_VALUE,
  CONSENT_CREATION_ERROR_MODAL,
} from "@/config/app-constant";
import { useFetchMTAModalData } from "@/hooks/api/cms/use-fetch-mta-modal-data";
import useMTAConsentHandler from "@/hooks/use-mta-consent-handler/use-mta-consent-handler";
import { useLoading } from "@/providers/loading-provider";
import { useAppSelector } from "@/store/hook";

import MTAConsentModal from "./mta-consent-modal";
import MTAConsentNoModal from "./mta-consent-no-modal";
import MTAConsentYesModal from "./mta-consent-yes-modal";
import MTAErrorModal from "./mta-error-modal";

type MenuItemType = {
  uniqueId: string;
  title: string;
  modalContentOne: string;
  modalCTAButtonLabelOne: string;
  modalCTAButtonLabelTwo: string;
  modalContentTwo?: string;
  modalMiddleContentTitle?: string;
  modalMiddleContentDescription?: string;
};

type RenewalMTAQuoteCreationModalProps = {
  isOpen: boolean;
  currentUsagePage?: string;
  handleClose?: () => void;
};

function RenewalMTAQuoteCreationModal({
  isOpen,
  currentUsagePage,
  handleClose,
}: Readonly<RenewalMTAQuoteCreationModalProps>) {
  const [isModalOpenConfig, setIsModalOpenConfig] = useState({
    isOpenModal: false,
    modalType: "",
  });
  const [selectedModalData, setSelectedModalData] = useState<MenuItemType>();
  const router = useRouter();
  const { createMTAError, data, updateConsent, isLoading } =
    useMTAConsentHandler();
  const { showLoading, hideLoading } = useLoading();
  const { attributes } = useAppSelector((state) => state.quote);
  const { paymentDetails } = attributes;
  const [retryCount, setRetryCount] = useState(0);
  const [mtaConsent, setMtaConsent] = useState(false);

  // Fetch modal data using custom hook
  const {
    data: modalDataResponse,
    loading: modalDataLoading,
    error: modalDataError,
  } = useFetchMTAModalData();

  const modalData = useMemo(
    () => modalDataResponse?.menuItems ?? [],
    [modalDataResponse?.menuItems]
  );
  const errorModalContentObject = useMemo(
    () =>
      modalDataResponse?.errorModalContent ?? {
        errorModalTitle: "",
        errorModalContent: "",
        errorModalCTAButtonLabel: "",
      },
    [modalDataResponse?.errorModalContent]
  );

  const isMTAPriceGreaterThanZero =
    Number(paymentDetails?.paymentOptions[0].cardPayment.card.mtaPrice) > 0;

  const incrementRetryCount = useCallback(() => {
    setRetryCount((previousRetryCount) => previousRetryCount + 1);
    updateConsent(mtaConsent);
    setIsModalOpenConfig({
      isOpenModal: false,
      modalType: "",
    });
  }, [updateConsent, mtaConsent]);

  const handleErrorResponse = () => {
    hideLoading();
    if (handleClose) handleClose();

    if (createMTAError && retryCount < 3) {
      setIsModalOpenConfig({
        isOpenModal: true,
        modalType: CONSENT_CREATION_ERROR_MODAL,
      });
    } else {
      setIsModalOpenConfig({
        isOpenModal: false,
        modalType: "",
      });

      router.push("/unable-to-create-mta");
    }
  };

  useEffect(() => {
    if (data?.attributes?.mtaMedicalConsent && !createMTAError) {
      const YES_MODAL_TYPE = isMTAPriceGreaterThanZero
        ? CONSENT_YES_MODAL
        : CONSENT_YES_MODAL_WITH_ZERO_VALUE;

      setIsModalOpenConfig({
        isOpenModal: true,
        modalType: data.attributes.mtaMedicalConsent.isTravellingBeforeRenewal
          ? YES_MODAL_TYPE
          : CONSENT_NO_MODAL,
      });
    } else if (createMTAError) {
      handleErrorResponse();
    }

    if (isLoading) {
      showLoading();
    } else {
      hideLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, createMTAError, isLoading]);

  useEffect(() => {
    if (isOpen) {
      setIsModalOpenConfig({
        isOpenModal: isOpen,
        modalType: CONSENT_MODAL,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (modalData.length > 0 && isModalOpenConfig.modalType) {
      const selectedModal = modalData.find(
        (item) => item.uniqueId === isModalOpenConfig.modalType
      );
      setSelectedModalData(selectedModal);
    }
  }, [modalData, isModalOpenConfig.modalType]);

  const onClickHandlerMTAConsentModal = useCallback(
    (consent: boolean) => {
      setMtaConsent(consent);
      updateConsent(consent);
    },
    [updateConsent]
  );

  const onClickHandlerMTAConsentYesNoModal = useCallback(
    (action: boolean) => {
      if (!action) {
        setIsModalOpenConfig({
          ...isModalOpenConfig,
          modalType: CONSENT_MODAL,
        });
      } else {
        setIsModalOpenConfig({
          isOpenModal: false,
          modalType: "",
        });
        if (handleClose) handleClose();
        if (currentUsagePage === "medical-dashboard") router.push("/");
      }
    },
    [isModalOpenConfig, handleClose, currentUsagePage, router]
  );

  const modalContent = useMemo(() => {
    if (isModalOpenConfig.modalType === CONSENT_MODAL) {
      return (
        <MTAConsentModal
          modalContentOne={selectedModalData?.modalContentOne ?? ""}
          modalContentTwo={selectedModalData?.modalContentTwo ?? ""}
          modalCancelBtnText={selectedModalData?.modalCTAButtonLabelTwo ?? ""}
          modalConfirmBtnText={selectedModalData?.modalCTAButtonLabelOne ?? ""}
          modalConfirmBtnClick={onClickHandlerMTAConsentModal}
        />
      );
    }

    if (
      isModalOpenConfig.modalType === CONSENT_YES_MODAL ||
      isModalOpenConfig.modalType === CONSENT_YES_MODAL_WITH_ZERO_VALUE
    ) {
      return (
        <MTAConsentYesModal
          modalContentOne={selectedModalData?.modalContentOne || ""}
          modalContentTwo={selectedModalData?.modalContentTwo || ""}
          modalMiddleContentTitle={
            selectedModalData?.modalMiddleContentTitle || ""
          }
          modalMiddleContentDescription={
            selectedModalData?.modalMiddleContentDescription || ""
          }
          modalCancelBtnText={selectedModalData?.modalCTAButtonLabelTwo || ""}
          modalConfirmBtnText={selectedModalData?.modalCTAButtonLabelOne || ""}
          modalConfirmBtnClick={onClickHandlerMTAConsentYesNoModal}
        />
      );
    }

    if (isModalOpenConfig.modalType === CONSENT_NO_MODAL) {
      return (
        <MTAConsentNoModal
          modalContentOne={selectedModalData?.modalContentOne || ""}
          modalCancelBtnText={selectedModalData?.modalCTAButtonLabelTwo || ""}
          modalConfirmBtnText={selectedModalData?.modalCTAButtonLabelOne || ""}
          modalConfirmBtnClick={onClickHandlerMTAConsentYesNoModal}
        />
      );
    }

    if (isModalOpenConfig.modalType === CONSENT_CREATION_ERROR_MODAL) {
      return (
        <MTAErrorModal
          modalSubText={errorModalContentObject?.errorModalContent || ""}
          modalConfirmBtnText={
            errorModalContentObject?.errorModalCTAButtonLabel || ""
          }
          modalConfirmBtnClick={incrementRetryCount}
        />
      );
    }

    return null;
  }, [
    isModalOpenConfig.modalType,
    onClickHandlerMTAConsentModal,
    onClickHandlerMTAConsentYesNoModal,
    incrementRetryCount,
    selectedModalData,
    errorModalContentObject,
  ]);

  return (
    <Modal
      className="md:p-spacing-xxxl p-spacing-l md:max-w-[416px] max-w-[326.5px] w-full"
      data-testid="modal"
      isOpen={isModalOpenConfig.isOpenModal}
      isDismissable={false}
      onOpenChange={(open: boolean) => {
        if (!open) {
          setIsModalOpenConfig({
            isOpenModal: false,
            modalType: "",
          });
        }
      }}
      underlayStyles={{
        zIndex: 9999,
        background: "rgba(26, 26, 26, 0.6)",
      }}
      modalStyles={{
        background: "white",
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        borderRadius: 8,
        maxHeight: "95%",
        overflowY: "auto",
      }}
    >
      <div className=" flex justify-start w-full flex-col">
        {isModalOpenConfig.modalType === CONSENT_CREATION_ERROR_MODAL ? (
          <RichText
            richText={errorModalContentObject?.errorModalTitle}
            className="text-text-title font-header-font md:text-3xl text-[23px] 
           md:leading-[34.1px] leading-[28px] mb-spacing-s text-center"
          />
        ) : (
          <h1
            className="text-text-title font-header-font md:text-3xl text-[23px] text-start 
           md:leading-[34.1px] leading-custom-size-compact mb-spacing-s"
            data-testid="toggleBtnLabelWrapper-testid"
          >
            {selectedModalData?.title}
          </h1>
        )}
        {modalContent}
      </div>
    </Modal>
  );
}

export default RenewalMTAQuoteCreationModal;
